import type { Money, SellingPlan, SellingPlanGroup, ShopifyConnection } from '@repo/shopify';
import { flattenConnection } from '@repo/shopify/client';

/**
 * Flattens a product's selling plan groups (created by Recharge) into the list
 * of recurring plans the Subscribe & Save selector offers. Ordered as Shopify
 * returns them, which follows the app's configured frequency order.
 */
/**
 * Products sold one-time only, whatever plans Recharge attaches to them. The
 * 5-stick sampler used to be the cart's auto-converting trial; it is now a
 * plain $6.50 purchase (Shopify no longer requires a plan on it), and no
 * surface — PDP, product card, cart upsell — may offer it as a subscription
 * again even if its plan group gets republished to the storefront channel.
 */
export const ONE_TIME_ONLY_HANDLES = ['workman-hydrate-sample-pack'] as const;

export function isOneTimeOnlyProduct(handle: string | null | undefined): boolean {
	const key = handle?.trim().toLowerCase() ?? '';
	return key.length > 0 && (ONE_TIME_ONLY_HANDLES as readonly string[]).includes(key);
}

export function getSubscriptionPlans(product: {
	handle?: string | null;
	sellingPlanGroups?: ShopifyConnection<SellingPlanGroup>;
}): SellingPlan[] {
	if (!product.sellingPlanGroups || isOneTimeOnlyProduct(product.handle)) return [];

	return flattenConnection(product.sellingPlanGroups)
		.flatMap((group) => flattenConnection(group.sellingPlans))
		.filter((plan) => plan.recurringDeliveries);
}

function toMoneyAmount(value: number, currencyCode: string): Money {
	return { amount: (Math.round(value * 100) / 100).toFixed(2), currencyCode };
}

type SellingPlanAdjustmentValue = SellingPlan['priceAdjustments'][number]['adjustmentValue'];

/**
 * Applies one price adjustment to the one-time price. Fixed-amount/fixed-price
 * adjustments in a different currency than the variant price (non-USD markets)
 * are ignored rather than mixed.
 */
function getAdjustedPrice(price: Money, adjustment: SellingPlanAdjustmentValue | undefined): Money {
	if (!adjustment) return price;

	const baseAmount = Number.parseFloat(price.amount);

	if ('adjustmentPercentage' in adjustment) {
		const discounted = baseAmount * (1 - adjustment.adjustmentPercentage / 100);
		return toMoneyAmount(Math.max(0, discounted), price.currencyCode);
	}

	if ('adjustmentAmount' in adjustment) {
		if (adjustment.adjustmentAmount.currencyCode !== price.currencyCode) return price;
		const discounted = baseAmount - Number.parseFloat(adjustment.adjustmentAmount.amount);
		return toMoneyAmount(Math.max(0, discounted), price.currencyCode);
	}

	if (adjustment.price.currencyCode !== price.currencyCode) return price;
	return adjustment.price;
}

/** Per-delivery price for the first order, from the plan's first price adjustment. */
export function getSellingPlanUnitPrice(price: Money, plan: SellingPlan): Money {
	return getAdjustedPrice(price, plan.priceAdjustments[0]?.adjustmentValue);
}

/**
 * Per-delivery price after any intro pricing ends, from the plan's last price
 * adjustment. Equals {@link getSellingPlanUnitPrice} for single-adjustment plans.
 */
export function getSellingPlanOngoingPrice(price: Money, plan: SellingPlan): Money {
	return getAdjustedPrice(
		price,
		plan.priceAdjustments[plan.priceAdjustments.length - 1]?.adjustmentValue,
	);
}

/** Rounded percent saved vs the one-time price, or null when the plan doesn't discount. */
export function getSellingPlanPercentOff(price: Money, plan: SellingPlan): number | null {
	const adjustment = plan.priceAdjustments[0]?.adjustmentValue;
	if (adjustment && 'adjustmentPercentage' in adjustment) {
		const percent = Math.round(adjustment.adjustmentPercentage);
		return percent > 0 ? percent : null;
	}

	const baseAmount = Number.parseFloat(price.amount);
	if (baseAmount <= 0) return null;

	const unitAmount = Number.parseFloat(getSellingPlanUnitPrice(price, plan).amount);
	const percent = Math.round(((baseAmount - unitAmount) / baseAmount) * 100);
	return percent > 0 ? percent : null;
}

const RECHARGE_FREQUENCY_OPTION_PATTERN = /^(\d+)-(day|week|month|year)s?$/i;

interface SellingPlanLabelLike {
	name: string;
	options?: { name: string | null; value: string | null }[];
}

/**
 * Delivery frequency as a phrase ("every month", "every 2 months"), parsed from
 * the Recharge "Order Frequency and Unit" option value like "6-month". Null when
 * the plan has no parseable frequency option.
 */
export function getSellingPlanFrequencyPhrase(plan: SellingPlanLabelLike): string | null {
	for (const option of plan.options ?? []) {
		const match = option.value?.match(RECHARGE_FREQUENCY_OPTION_PATTERN);
		if (!match) continue;
		const count = Number.parseInt(match[1] as string, 10);
		const unit = (match[2] as string).toLowerCase();
		return count === 1 ? `every ${unit}` : `every ${count} ${unit}s`;
	}

	return null;
}

const FREQUENCY_UNIT_DAYS: Record<string, number> = {
	day: 1,
	week: 7,
	month: 30,
	year: 365,
};

/**
 * Delivery interval in days (a month counts as 30), parsed from the Recharge
 * "Order Frequency and Unit" option value like "6-month". Null when the plan
 * has no parseable frequency option. Used to order frequency options and to
 * match a plan against the bundle's supply duration.
 */
export function getSellingPlanIntervalDays(plan: SellingPlanLabelLike): number | null {
	for (const option of plan.options ?? []) {
		const match = option.value?.match(RECHARGE_FREQUENCY_OPTION_PATTERN);
		if (!match) continue;
		const count = Number.parseInt(match[1] as string, 10);
		const unitDays = FREQUENCY_UNIT_DAYS[(match[2] as string).toLowerCase()];
		if (unitDays) return count * unitDays;
	}

	return null;
}

/**
 * The delivery interval pre-selected wherever a plan must be chosen for the
 * shopper. Hydrate ships a 32-stick bag, and a 45-day cadence matches how fast
 * most subscribers actually get through one.
 */
export const DEFAULT_SELLING_PLAN_INTERVAL_DAYS = 45;

/**
 * The plan to pre-select in buy boxes and upsells: the 45-day delivery when
 * offered, else the shortest known interval, else the first plan. Never trust
 * Shopify's plan order — Recharge returns frequencies in creation order.
 */
export function getDefaultSellingPlan<T extends SellingPlanLabelLike>(plans: T[]): T | null {
	let shortest: T | null = null;
	let shortestDays = Number.POSITIVE_INFINITY;
	for (const plan of plans) {
		const days = getSellingPlanIntervalDays(plan);
		if (days === DEFAULT_SELLING_PLAN_INTERVAL_DAYS) return plan;
		if (days !== null && days < shortestDays) {
			shortest = plan;
			shortestDays = days;
		}
	}
	return shortest ?? plans[0] ?? null;
}

/**
 * Human label for the delivery frequency. Recharge plan NAMES are unreliable —
 * live data shows a 6-month plan named "Delivery every month" — but the real
 * frequency is encoded in an "Order Frequency and Unit" option value like
 * "6-month", so parse that first and only fall back to the name.
 */
export function getSellingPlanLabel(plan: SellingPlanLabelLike): string {
	const phrase = getSellingPlanFrequencyPhrase(plan);
	if (phrase) return `Delivery ${phrase}`;

	return plan.name || 'Subscription';
}

/**
 * Dropdown option label for a plan's delivery frequency, always with an
 * explicit count: "1 Month", "2 Months", "15 Days" — never a bare "Month".
 * Falls back to the plan name when no frequency option parses.
 */
export function getSellingPlanFrequencyOptionLabel(plan: SellingPlanLabelLike): string {
	const phrase = getSellingPlanFrequencyPhrase(plan) ?? plan.name ?? 'Subscription';
	const stripped = phrase.replace(/^(delivery\s+)?every\s+/i, '');
	const counted = /^\d/.test(stripped) ? stripped : `1 ${stripped}`;
	return counted.replace(/\b\w/g, (letter) => letter.toUpperCase());
}
